import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const STEPS = ['Role', 'Profile', 'Get Started'];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center gap-3 mb-10">
    {STEPS?.map((label, i) => (
      <React.Fragment key={i}>
        <div className="flex flex-col items-center gap-1">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
            style={{
              background: i < currentStep
                ? 'rgba(170,255,0,0.2)'
                : i === currentStep
                ? '#AAFF00' :'rgba(255,255,255,0.06)',
              color: i === currentStep ? '#0A0A0B' : i < currentStep ? '#AAFF00' : 'rgba(255,255,255,0.4)',
              border: i < currentStep ? '1px solid #AAFF00' : i === currentStep ? 'none' : '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {i < currentStep ? <Icon name="Check" size={14} /> : i + 1}
          </div>
          <span
            className="text-xs font-medium"
            style={{ color: i === currentStep ? '#AAFF00' : 'rgba(255,255,255,0.35)' }}
          >
            {label}
          </span>
        </div>
        {i < STEPS?.length - 1 && (
          <div
            className="w-12 h-px mb-5 transition-all duration-500"
            style={{ background: i < currentStep ? '#AAFF00' : 'rgba(255,255,255,0.1)' }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Step 1: Role Selection
const RoleSelection = ({ selectedRole, onSelect }) => {
  const roles = [
    {
      id: 'trainer',
      icon: 'Dumbbell',
      title: 'Personal Trainer',
      subtitle: 'I coach and manage clients',
      perks: ['Build workout & nutrition plans', 'Track client progress', 'Manage your client roster'],
      color: '#FF6B00',
      glow: 'rgba(255,107,0,0.15)',
    },
    {
      id: 'client',
      icon: 'User',
      title: 'Client / Athlete',
      subtitle: 'I train and track my progress',
      perks: ['Follow assigned programmes', 'Log workouts & nutrition', 'Monitor personal progress'],
      color: '#AAFF00',
      glow: 'rgba(170,255,0,0.15)',
    },
  ];

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">How will you use FormForge?</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
          Choose your role — you can always change this later in settings.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {roles?.map((role) => {
          const isSelected = selectedRole === role?.id;
          return (
            <button
              key={role?.id}
              onClick={() => onSelect(role?.id)}
              className="text-left p-5 rounded-xl transition-all duration-200 focus:outline-none"
              style={{
                background: isSelected ? role?.glow : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isSelected ? role?.color : 'rgba(255,255,255,0.08)'}`,
                boxShadow: isSelected ? `0 0 24px ${role?.glow}` : 'none',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: isSelected ? role?.color : 'rgba(255,255,255,0.06)' }}
                >
                  <Icon name={role?.icon} size={20} color={isSelected ? '#0A0A0B' : role?.color} />
                </div>
                <div>
                  <div className="font-semibold text-white text-sm">{role?.title}</div>
                  <div className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{role?.subtitle}</div>
                </div>
                {isSelected && (
                  <div className="ml-auto w-5 h-5 rounded-full flex items-center justify-center" style={{ background: role?.color }}>
                    <Icon name="Check" size={12} color="#0A0A0B" />
                  </div>
                )}
              </div>
              <ul className="space-y-1.5">
                {role?.perks?.map((perk, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    <span style={{ color: role?.color }}>•</span>
                    {perk}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Step 2: Profile Setup
const ProfileSetup = ({ role, profileData, onChange }) => {
  const isTrainer = role === 'trainer';

  const trainerFields = [
    { name: 'specialty', label: 'Training Specialty', type: 'select', options: ['Strength & Conditioning', 'Weight Loss', 'Sports Performance', 'Rehabilitation', 'Nutrition Coaching', 'General Fitness'] },
    { name: 'experience', label: 'Years of Experience', type: 'select', options: ['Less than 1 year', '1–3 years', '3–5 years', '5–10 years', '10+ years'] },
    { name: 'clientCount', label: 'How many clients do you currently have?', type: 'select', options: ['Just starting out', '1–5 clients', '6–15 clients', '16–30 clients', '30+ clients'] },
  ];

  const clientFields = [
    { name: 'fitnessGoal', label: 'Primary Fitness Goal', type: 'select', options: ['Lose Weight', 'Build Muscle', 'Improve Endurance', 'Increase Flexibility', 'General Health', 'Sports Performance'] },
    { name: 'activityLevel', label: 'Current Activity Level', type: 'select', options: ['Sedentary (desk job, little exercise)', 'Lightly Active (1–2 days/week)', 'Moderately Active (3–4 days/week)', 'Very Active (5+ days/week)'] },
    { name: 'trainingFrequency', label: 'How often do you want to train?', type: 'select', options: ['2–3 times per week', '4–5 times per week', '6–7 times per week', 'Daily'] },
  ];

  const fields = isTrainer ? trainerFields : clientFields;

  return (
    <div>
      <div className="text-center mb-8">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
          style={{ background: isTrainer ? 'rgba(255,107,0,0.15)' : 'rgba(170,255,0,0.15)' }}
        >
          <Icon name={isTrainer ? 'Dumbbell' : 'User'} size={22} color={isTrainer ? '#FF6B00' : '#AAFF00'} />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {isTrainer ? 'Tell us about your practice' : 'Set up your profile'}
        </h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {isTrainer
            ? 'Help us tailor FormForge to your coaching style.' :'We\'ll personalise your experience around your goals.'}
        </p>
      </div>
      <div className="space-y-4">
        {fields?.map((field) => (
          <div key={field?.name}>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {field?.label}
            </label>
            <select
              value={profileData?.[field?.name] || ''}
              onChange={(e) => onChange(field?.name, e?.target?.value)}
              className="w-full px-4 py-3 rounded-lg text-sm focus:outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: profileData?.[field?.name] ? 'white' : 'rgba(255,255,255,0.35)',
              }}
            >
              <option value="" disabled style={{ background: '#1a1a1b' }}>Select an option</option>
              {field?.options?.map((opt) => (
                <option key={opt} value={opt} style={{ background: '#1a1a1b', color: 'white' }}>{opt}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

// Step 3: First Action Prompt
const FirstAction = ({ role }) => {
  const isTrainer = role === 'trainer';

  const actions = isTrainer
    ? [
        { icon: 'UserPlus', label: 'Add your first client', desc: 'Invite a client and start building their programme', path: '/trainer-dashboard', color: '#AAFF00' },
        { icon: 'Dumbbell', label: 'Build a workout plan', desc: 'Create a reusable programme from the workout builder', path: '/trainer-workout-builder', color: '#FF6B00' },
        { icon: 'LayoutDashboard', label: 'Explore your dashboard', desc: 'Get an overview of your coaching hub', path: '/trainer-dashboard', color: '#00D4FF' },
      ]
    : [
        { icon: 'Dumbbell', label: 'View today\'s workout', desc: 'See what\'s planned for you today', path: '/client-workout-dashboard', color: '#AAFF00' },
        { icon: 'BarChart2', label: 'Log your first activity', desc: 'Record a workout or activity to start tracking', path: '/activity-tracking', color: '#FF6B00' },
        { icon: 'LayoutDashboard', label: 'Explore your dashboard', desc: 'Get familiar with your personal hub', path: '/dashboard', color: '#00D4FF' },
      ];

  return (
    <div>
      <div className="text-center mb-8">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(170,255,0,0.12)' }}>
          <Icon name="Zap" size={22} color="#AAFF00" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">You're ready to go 🎉</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
          Pick where you'd like to start — you can always navigate anywhere from your dashboard.
        </p>
      </div>
      <div className="space-y-3">
        {actions?.map((action, i) => (
          <ActionCard key={i} action={action} role={role} />
        ))}
      </div>
    </div>
  );
};

const ActionCard = ({ action, role }) => {
  const navigate = useNavigate();
  const handleClick = () => {
    // Set tour trigger flag when navigating to role dashboard
    if (action?.path === '/trainer-dashboard') {
      localStorage.setItem('ff_show_tour_trainer', 'true');
    } else if (action?.path === '/dashboard') {
      localStorage.setItem('ff_show_tour_client', 'true');
    }
    navigate(action?.path);
  };
  return (
    <button
      onClick={handleClick}
      className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-200 group"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `rgba(${action?.color === '#AAFF00' ? '170,255,0' : action?.color === '#FF6B00' ? '255,107,0' : '0,212,255'},0.06)`;
        e.currentTarget.style.borderColor = action?.color;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
      }}
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${action?.color}18` }}>
        <Icon name={action?.icon} size={20} color={action?.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white text-sm">{action?.label}</div>
        <div className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>{action?.desc}</div>
      </div>
      <Icon name="ArrowRight" size={16} color="rgba(255,255,255,0.3)" />
    </button>
  );
};

// Main Onboarding Page
const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [role, setRole] = useState('');
  const [profileData, setProfileData] = useState({});
  const [saving, setSaving] = useState(false);

  const handleProfileChange = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    if (step === 0) return !!role;
    if (step === 1) {
      const requiredFields = role === 'trainer'
        ? ['specialty', 'experience', 'clientCount']
        : ['fitnessGoal', 'activityLevel', 'trainingFrequency'];
      return requiredFields?.every((f) => !!profileData?.[f]);
    }
    return true;
  };

  const handleNext = async () => {
    if (step === 1) {
      setSaving(true);
      try {
        await updateProfile({
          role,
          onboarding_completed: true,
          onboarding_data: profileData,
        });
      } catch (e) {
        console.error('Onboarding save error:', e);
      } finally {
        setSaving(false);
      }
    }
    if (step < 2) setStep((s) => s + 1);
  };

  const handleSkip = () => {
    if (step < 2) setStep((s) => s + 1);
    else {
      // Set tour flag before navigating to dashboard
      if (role === 'trainer') {
        localStorage.setItem('ff_show_tour_trainer', 'true');
      } else if (role === 'client') {
        localStorage.setItem('ff_show_tour_client', 'true');
      }
      navigate(role === 'trainer' ? '/trainer-dashboard' : '/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: '#0A0A0B' }}>
      {/* Ambient glow */}
      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(170,255,0,0.06) 0%, transparent 70%)', filter: 'blur(40px)' }}
      />
      <div className="w-full max-w-lg relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#AAFF00' }}>
              <Icon name="Zap" size={16} color="#0A0A0B" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">FormForge</span>
          </div>
        </div>

        <StepIndicator currentStep={step} />

        {/* Card */}
        <div
          className="rounded-2xl p-6 lg:p-8"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {step === 0 && (
            <RoleSelection selectedRole={role} onSelect={setRole} />
          )}
          {step === 1 && (
            <ProfileSetup role={role} profileData={profileData} onChange={handleProfileChange} />
          )}
          {step === 2 && (
            <FirstAction role={role} />
          )}

          {/* Actions */}
          {step < 2 && (
            <div className="flex items-center justify-between mt-8 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                onClick={handleSkip}
                className="text-sm transition-colors"
                style={{ color: 'rgba(255,255,255,0.35)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
              >
                Skip for now
              </button>
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                loading={saving}
                className="px-8"
                style={{
                  background: canProceed() ? '#AAFF00' : 'rgba(255,255,255,0.06)',
                  color: canProceed() ? '#0A0A0B' : 'rgba(255,255,255,0.3)',
                  border: 'none',
                  fontWeight: 600,
                }}
              >
                {step === 1 ? 'Save & Continue' : 'Continue'}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                onClick={() => {
                  // Set tour flag so dashboard shows the guided tour
                  if (role === 'trainer') {
                    localStorage.setItem('ff_show_tour_trainer', 'true');
                  } else {
                    localStorage.setItem('ff_show_tour_client', 'true');
                  }
                  navigate(role === 'trainer' ? '/trainer-dashboard' : '/dashboard');
                }}
                className="w-full text-sm py-2 transition-colors"
                style={{ color: 'rgba(255,255,255,0.35)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
              >
                Go to my dashboard instead →
              </button>
            </div>
          )}
        </div>

        {/* Step count */}
        <p className="text-center text-xs mt-4" style={{ color: 'rgba(255,255,255,0.2)' }}>
          Step {step + 1} of {STEPS?.length}
        </p>
      </div>
    </div>
  );
};

export default OnboardingPage;
